import { useState, useEffect, useRef } from "react";
import {
  Modal,
  Stack,
  Button,
  Group,
  Text,
  TextInput,
  ThemeIcon,
  Divider,
  Box,
  Kbd,
} from "@mantine/core";
import { IconCheck, IconBarcode, IconArrowLeft } from "@tabler/icons-react";

interface SignOffSuccessModalProps {
  opened: boolean;
  onClose: () => void;
  caseNumber: string;
  signedBy: string;
  signedAt: string;
  onScanBarcode?: (barcode: string) => void;
  onReturnToQueue?: () => void;
}

export function SignOffSuccessModal({
  opened,
  onClose,
  caseNumber,
  signedBy,
  signedAt,
  onScanBarcode,
  onReturnToQueue,
}: SignOffSuccessModalProps) {
  const [barcodeValue, setBarcodeValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const formattedSignedAt = new Date(signedAt).toLocaleString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  useEffect(() => {
    if (opened) {
      setBarcodeValue("");
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [opened]);

  const handleBarcodeSubmit = () => {
    const trimmed = barcodeValue.trim();
    if (!trimmed) return;
    onScanBarcode?.(trimmed);
    setBarcodeValue("");
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleBarcodeSubmit();
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="sm"
      centered
      closeOnClickOutside={false}
      withCloseButton
    >
      <Stack gap="md" py="xs">
        <Box ta="center">
          <ThemeIcon
            size={48}
            radius="xl"
            variant="light"
            color="green"
            mb="sm"
          >
            <IconCheck size={26} />
          </ThemeIcon>
          <Text size="lg" fw={600}>
            Signed Out
          </Text>
          <Text size="sm" c="dimmed" mt={4}>
            {caseNumber}
          </Text>
          <Text size="xs" c="dimmed">
            {signedBy} &middot; {formattedSignedAt}
          </Text>
        </Box>

        <Divider />

        <Box>
          <TextInput
            ref={inputRef}
            placeholder="Scan or type next barcode..."
            value={barcodeValue}
            onChange={(e) => setBarcodeValue(e.currentTarget.value)}
            onKeyDown={handleKeyDown}
            leftSection={<IconBarcode size={16} />}
            rightSection={
              barcodeValue.trim() ? <Kbd size="xs">Enter</Kbd> : null
            }
            rightSectionWidth={barcodeValue.trim() ? 52 : undefined}
            size="md"
          />
        </Box>

        <Group justify="center">
          <Button
            variant="subtle"
            color="gray"
            size="sm"
            leftSection={<IconArrowLeft size={14} />}
            onClick={() => {
              onClose();
              onReturnToQueue?.();
            }}
          >
            Return to Queue
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
